/*
 * analytics.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Topheader from "../../Layout/header/header";
import "./analytics.css";
import { Box, Typography, CircularProgress, Backdrop } from "@mui/material";
import { ArrowDropDownRounded, ArrowDropUpRounded } from "@mui/icons-material";
import APIManager from "../../Services/ApiManager/ApiManager";
import PopupDialog from "../../Components/PopupDialog";
import { getConfig } from "../../config";

const NAV_ITEM_MAP = {
  activity: "activity",
  sessions: "sessions",
  device: "device",
  applications: "applications",
  topApplications: "topApplications",
  vehicleReport: "vehicleReport",
  storeMetrics: "storeMetrics",
  dataConsumption: "dataConsumption",
  paymentSummary: "paymentSummary",
  subscriptions: "Subscriptions",
  inAppPurchase: "In-app Purchase",
  appCrash: "appCrash",
  featuresActivity: "featuresActivity",
};

const ACTIVE_NAV_STYLE = { cursor: "pointer", color: " #0476E2", background: " #E5F3FF" };
const DEFAULT_NAV_STYLE = { cursor: "pointer" };

function getErrorHeader(message) {
  return message === "jwt expired" ? "401 Token Expired" : "Error";
}

function getTableauPaths(isOemAdmin, parsedConfig) {
  if (isOemAdmin) {
    return {
      paths: parsedConfig.settings.tableau.oemAdminPaths,
      url: parsedConfig.settings.tableau.url,
    };
  }
  return {
    paths: parsedConfig[0].config.tableau.adminPaths,
    url: parsedConfig[0].config.tableau.url,
  };
}

function NavItem({ clickKey, activeId, label, activeNavItem, onNavClick }) {
  const isActive = activeNavItem === activeId;
  return (
    <button
      type="button"
      className="nav-item"
      style={isActive ? ACTIVE_NAV_STYLE : DEFAULT_NAV_STYLE}
      onClick={() => onNavClick(clickKey)}
    >
      <Typography sx={{ fontWeight: isActive ? "bold" : "400", fontSize: "14px" }}>
        {label}
      </Typography>
    </button>
  );
}

NavItem.propTypes = {
  clickKey: PropTypes.string.isRequired,
  activeId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  activeNavItem: PropTypes.string.isRequired,
  onNavClick: PropTypes.func.isRequired,
};

function NavSection({ title, isOpen, onToggle, children }) {
  return (
    <div className="nav-section">
      <button type="button" className="nav-header" onClick={onToggle}>
        <Box display="flex" alignItems="center">
          {isOpen ? (
            <ArrowDropUpRounded sx={{ color: "#12141599" }} />
          ) : (
            <ArrowDropDownRounded sx={{ color: "#12141599" }} />
          )}
          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
            {title}
          </Typography>
        </Box>
      </button>
      {isOpen && <div className="nav-items">{children}</div>}
    </div>
  );
}

NavSection.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default function Analytics() {
  const [loader, setLoader] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [usageOpen, setUsageOpen] = useState(true);
  const [activeNavItem, setActiveNavItem] = useState("activity");
  const [ticket, setTicket] = useState("");
  const [tableauRef, setTableauRef] = useState(null);
  const isOemAdmin =
    getConfig().REACT_APP_PORTAL === "oem-admin" ||
    getConfig().REACT_APP_PORTAL === "domain-approver";
  let tableauViz;

  const buildUrl = () => {
    const portalConfig = sessionStorage.getItem("portalConfig");
    const parsedConfig = JSON.parse(portalConfig);
    const { paths, url } = getTableauPaths(isOemAdmin, parsedConfig);
    return url + paths.ticket.replace("%ticket%", ticket) + paths[activeNavItem];
  };

  const initTableau = () => {
    const embedUrl = buildUrl();
    if (!embedUrl || !tableauRef || !globalThis.tableau) return;
    tableauViz?.dispose();
    const options = {
      width: "100%",
      height: "100%",
      hideTabs: true,
      hideToolbar: true,
      scrolling: "no",
      onFirstInteractive: function () {
        const workbookReports = tableauViz.getWorkbook();
        workbookReports.getActiveSheet();
      },
    };
    try {
      tableauViz = new globalThis.tableau.Viz(tableauRef, embedUrl, options);
    } catch (e) {
      console.log("[WARNING!] Tableau embed exception:", e);
    }
  };

  const refreshTableauTicket = async () => {
    setLoader(true);

    try {
      const isOemAdmin = getConfig().REACT_APP_PORTAL === "oem-admin";
      const response = isOemAdmin
        ? await APIManager.postAdminTableauUrl()
        : await APIManager.postAdminTableauUrlGobalAdmin();

      setTicket(response);
      return response;
    } catch (error) {
      console.error("Error fetching tableau ticket:", error);
      setComponentName("appError");
      setErrorMessage(error.message);
      setHeaderMessage(getErrorHeader(error.message));
      setOpenErrorPopup(true);
      return null;
    } finally {
      setLoader(false);
    }
  };

  const handleClick = async (itemName) => {
    const newTicket = await refreshTableauTicket();
    if (!newTicket) return;
    const navItem = NAV_ITEM_MAP[itemName];
    if (navItem) setActiveNavItem(navItem);
  };
  useEffect(() => {
    refreshTableauTicket();
  }, []);

  useEffect(() => {
    if (ticket) {
      initTableau();
    }

    return () => {
      if (tableauViz) {
        tableauViz.dispose();
      }
    };
  }, [ticket, activeNavItem]);

  return (
    <>
      {loader && (
        <Backdrop
          open={loader}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Topheader />
      <div style={{ display: "flex" }}>
        <div className="navbar-container">
          <nav className="custom-navbar">
            <NavSection title="Usage" isOpen={usageOpen} onToggle={() => setUsageOpen(!usageOpen)}>
              <NavItem clickKey="activity" activeId="activity" label="Activity" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="sessions" activeId="sessions" label="Sessions" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="device" activeId="device" label="Devices" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="applications" activeId="applications" label="Apps" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="topApplications" activeId="topApplications" label="Top Apps" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="vehicleReport" activeId="vehicleReport" label="Active Vehicles" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="storeMetrics" activeId="storeMetrics" label="Store Metrics" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="dataConsumption" activeId="dataConsumption" label="Data Consumption" activeNavItem={activeNavItem} onNavClick={handleClick} />
              <NavItem clickKey="appCrash" activeId="appCrash" label="Crash Report" activeNavItem={activeNavItem} onNavClick={handleClick} />
            </NavSection>
          </nav>
        </div>

        <div className="analytics-container">
          <div
            ref={(ref) => setTableauRef(ref)}
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          />
        </div>
        <PopupDialog
          openErrorPopup={openErrorPopup}
          errorMessage={errorMessage}
          headerMessage={headerMessage}
          componentName={componentName}
          onClose={() => setOpenErrorPopup(false)}
        />
      </div>
    </>
  );
}
